import { ThreadGoalsControl } from "@/shared/components/agentThread/ThreadGoalsControl";
import { ThreadVisibilityControl } from "@/shared/components/agentThread/ThreadVisibilityControl";
import { FormattedDateTime } from "@/shared/datetime";
import { type AgentThreadRecord } from "@/shared/domain/agentThread";
import { Clock, Info } from "@/shared/foundation/icons";
import { Popover } from "@/shared/foundation/primitives";

import { OpenThreadInSidebarButton } from "./OpenThreadInSidebarButton";
import styles from "./ThreadHeaderBar.module.css";

interface ThreadHeaderBarProps {
  thread: AgentThreadRecord;
}

/**
 * The strip above the conversation: what this thread is on the left, what you
 * can do with it on the right.
 *
 * The thread id is the only field spelled out. Bug reports, support threads,
 * and database queries are keyed on it, and unlike the title it appears
 * nowhere else on the page — the breadcrumb overhead carries the title.
 *
 * Who started the thread and when sits behind the info icon. It answers an
 * occasional question, and laid out permanently it would push the controls
 * into the middle of the row.
 */
export function ThreadHeaderBar({ thread }: ThreadHeaderBarProps) {
  return (
    <div className={styles.bar}>
      <div className={styles.threadId}>
        <span className={styles.metadataLabel}>Thread ID:</span>
        <span className={styles.metadataValue}>{thread.thread_id}</span>
      </div>

      <Popover>
        <Popover.Trigger
          openOnHover
          render={
            <button
              type="button"
              className={styles.infoTrigger}
              aria-label="Thread details"
            >
              <Info className={styles.infoTriggerIcon} aria-hidden="true" />
            </button>
          }
        />
        <Popover.Content
          align="start"
          aria-label="Thread details"
          className={styles.infoCard}
        >
          <div className={styles.info}>
            <div className={styles.infoRow}>
              <span>{thread.created_by}</span>
            </div>
            <div className={styles.infoRow}>
              <Clock className={styles.infoIcon} aria-hidden="true" />
              <FormattedDateTime value={thread.created} />
            </div>
          </div>
        </Popover.Content>
      </Popover>

      <div className={styles.actions}>
        <ThreadGoalsControl thread={thread} variant="pill" />
        <ThreadVisibilityControl thread={thread} variant="pill" />
        <OpenThreadInSidebarButton thread={thread} />
      </div>
    </div>
  );
}
